"use client";

import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Lenis from "@studio-freight/lenis";

/* ================= IMAGES ================= */

import img1 from "../../assets/images/expedition-gear/backpacking/backpack4.png";
import img2 from "../../assets/images/expedition-gear/backpacking/backpack6.png";
import img3 from "../../assets/images/expedition-gear/backpacking/backpack8.png";
import img4 from "../../assets/images/expedition-gear/backpacking/backpack7.png";

/* ================= DATA ================= */

const cards = [
  {
    id: 1,
    image: img1,
    title: "",
    description:
      "",
  },

  {
    id: 2,
    image: img3,
    title: "Bottom Layer - Summit Essentials",
       description:
      "Bulky, lightweight items at the bottom to keep your pack stable.",
  },
    {
    id: 3,
    image: img2,
    title: "Middle Layer - Core Weight Zone",
    description:
      "Food, fuel, and dense gear form the structural center of the pack.",
  },
  {
    id: 4,
    image: img4,
    title: "Top Layer - Quick Access Gear",
   description:
      "Weather protection, navigation, first-aid and safety tools stay within immediate reach.",
 
  },
];

const sectionContent = {
  badge: "Packing System",
  heading: "How Expedition Packs Are Engineered",
  subheading:
    "Each layer has a purpose. Scroll to reveal how weight, access, and balance are optimized.",
};

/* ================= CARD ================= */

function Card({ card, index, scrollYProgress, total }) {
  const step = 1 / total;
  const start = index * step;
  const end = (index + 1) * step;

  const rawY = useTransform(scrollYProgress, [start, end], ["110%", "0%"], {
    clamp: true,
  });
  const y = useSpring(rawY, { stiffness: 120, damping: 35, mass: 0.6 });

  const rawScale = useTransform(scrollYProgress, [start, end], [0.95, 1], {
    clamp: true,
  });
  const scale = useSpring(rawScale, {
    stiffness: 120,
    damping: 35,
    mass: 0.6,
  });

  // First card stays visible, animation starts from card 2
  const motionStyle =
    index === 0
      ? { zIndex: index + 1 }
      : { y, scale, zIndex: index + 1 };

  return (
    <motion.div
      style={motionStyle}
      className="absolute inset-0 rounded-3xl overflow-hidden
      
                 bg-[#e3e3e3]
                 flex flex-col justify-end p-10 will-change-transform"
    >
      {/* IMAGE */}
      <div className="relative inset-0">
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-full object-contain"
        />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 text-center">
        <h3 className="text-2xl text-[#162a38] font-bold mb-3">{card.title}</h3>
        <p className="text-gray-900 max-w-lg mx-auto">
          {card.description}
        </p>
      </div>
    </motion.div>
  );
}

/* ================= SECTION ================= */

export default function StackedCardsPinned() {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Lock screen until all cards animate
  const sectionHeight = `${cards.length * 110}vh`;

  /* ================= LENIS (OPTIONAL) ================= */
  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.7,
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
<section
  ref={containerRef}
  style={{ height: sectionHeight }}
  className="relative bg-[#e3e3e3] text-black"
>
  {/* STICKY WRAPPER */}
  <div className="sticky top-0 h-screen flex items-center px-6">
    <div className="mx-auto w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 lg:gap-16 items-center">
      
      {/* LEFT: HEADER / CONTENT */}
      <div className="relative z-10">
        <motion.div
          className="inline-block px-4 py-1 mb-6 border border-black/20 rounded-full text-xs uppercase tracking-widest"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {sectionContent.badge}
        </motion.div>

        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-6 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {sectionContent.heading}
        </motion.h2>

        <motion.p
          className="text-black/70 max-w-xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {sectionContent.subheading}
        </motion.p>
      </div>

      {/* RIGHT: CARD STACK */}
      <div className="relative w-full h-[380px] lg:h-[410px] flex items-center justify-center">
        <div className="relative w-full max-w-xl h-full">
          {cards.map((card, idx) => (
            <Card
              key={card.id}
              card={card}
              index={idx}
              scrollYProgress={scrollYProgress}
              total={cards.length}
            />
          ))}
        </div>
      </div>

    </div>
  </div>
</section>

  );
}
